import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  ButtonGroup,
  TextField,
  InputAdornment,
  Alert,
  Tooltip,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import { accountingApi } from '../../../config/apiClient';
import { useAuth } from '../../../contexts/AuthContext';
import { StatsCardItem, StatsCardsContainer, STATS_COLORS } from '../shared/StatsCard';
import type { DataTableColumn } from '../shared/AccountingDataTable';
import InventoryStyleListTable from './components/InventoryStyleListTable';
import { useTranslation } from 'react-i18next';
import {
  ProductRecipeMenuService,
  ProductMenuListItem,
  ProductListSummary,
} from './services/productRecipeMenuService';
import { ProductMenuModal } from './components/ProductMenuModal';
import { InventoryItem } from './types/inventory.types';
import GuideButton from '../../common/guide/GuideButton';

/**
 * Ürün Menüleri (kombo/paket tanımları) listesi.
 *
 * Düzen ve tablo kabuğu stok yönetimi ekranıyla aynıdır. Satırdan mevcut
 * `ProductMenuModal` açılır, böylece kalem ekleme/çıkarma mantığı tek yerde kalır.
 */
const ProductMenus: React.FC = () => {
  const { t, i18n } = useTranslation('accounting');
  const locale = i18n.language?.toLowerCase().startsWith('en') ? 'en-US' : 'tr-TR';
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { userDetails, warehouses, categories } = useAuth();
  const companyId = userDetails?.companyId || null;

  const [menus, setMenus] = useState<ProductMenuListItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);

  const [summary, setSummary] = useState<ProductListSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState<boolean>(true);

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [sortColumn, setSortColumn] = useState<string>('menuName');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const [modalItem, setModalItem] = useState<InventoryItem | null>(null);
  const [openingMenuId, setOpeningMenuId] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setPage(0);
  }, [statusFilter, sortColumn, sortDirection]);

  const fetchMenus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await ProductRecipeMenuService.getPagedMenus({
        page: page + 1,
        pageSize: rowsPerPage,
        search: debouncedSearchTerm,
        isActive: statusFilter === 'all' ? null : statusFilter === 'active',
        sortColumn,
        sortDirection,
      });
      setMenus(result.items);
      setTotalCount(result.totalCount);
    } catch (err: any) {
      console.error('❌ [MENUS] Menüler yüklenirken hata:', err);
      setError(err?.response?.data?.message || err?.message || t('stock.productMenusPage.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearchTerm, statusFilter, sortColumn, sortDirection, t]);

  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      setSummary(await ProductRecipeMenuService.getMenuSummary());
    } catch (err) {
      // Özet kartları bilgilendirme amaçlı; hatası listeyi engellemesin.
      console.error('❌ [MENUS] Özet alınamadı:', err);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMenus();
  }, [fetchMenus]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const handleRefresh = () => {
    fetchMenus();
    fetchSummary();
  };

  const formatDate = (value?: string | null): string => {
    if (!value) return '-';
    return new Date(value).toLocaleString(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleOpenMenu = async (row: ProductMenuListItem) => {
    setOpeningMenuId(row.id);
    try {
      const response = await accountingApi.get(`/api/Inventory/items/${row.inventoryItemId}`);
      setModalItem(response.data as InventoryItem);
    } catch (err) {
      console.error('❌ [MENUS] Ürün kartı çekilemedi:', err);
      setError(t('stock.productMenusPage.errors.itemLoadFailed'));
    } finally {
      setOpeningMenuId(null);
    }
  };

  const handleExportToExcel = () => {
    const exportData = menus.map((m) => ({
      [t('stock.productMenusPage.columns.menuName')]: m.menuName,
      [t('stock.productMenusPage.columns.menuCode')]: m.menuCode || '',
      [t('stock.productMenusPage.columns.product')]: m.inventoryItemName || '',
      [t('stock.productMenusPage.columns.stockCode')]: m.inventoryItemStockCode || '',
      [t('stock.productMenusPage.columns.itemCount')]: m.itemCount,
      [t('stock.productMenusPage.columns.optionGroupCount')]: m.optionGroupCount,
      [t('stock.productMenusPage.columns.status')]: m.isActive
        ? t('stock.productMenusPage.status.active')
        : t('stock.productMenusPage.status.inactive'),
      [t('stock.productMenusPage.columns.createdAt')]: formatDate(m.createdAt),
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t('stock.productMenusPage.title'));
    XLSX.writeFile(wb, `product_menus_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const columns = useMemo<DataTableColumn<ProductMenuListItem>[]>(() => [
    {
      id: 'menuName',
      label: t('stock.productMenusPage.columns.menuName'),
      field: 'menuName',
      sortable: true,
      minWidth: '200px',
      render: (row) => (
        <>
          <Typography variant="body2" fontWeight="medium">{row.menuName}</Typography>
          {row.menuCode && (
            <Typography variant="caption" color="text.secondary">{row.menuCode}</Typography>
          )}
        </>
      ),
    },
    {
      id: 'itemName',
      label: t('stock.productMenusPage.columns.product'),
      field: 'inventoryItemName',
      sortable: true,
      minWidth: '200px',
      render: (row) => (
        <>
          <Typography variant="body2">{row.inventoryItemName || '-'}</Typography>
          {row.inventoryItemStockCode && (
            <Typography variant="caption" color="text.secondary">{row.inventoryItemStockCode}</Typography>
          )}
        </>
      ),
    },
    {
      id: 'itemCount',
      label: t('stock.productMenusPage.columns.itemCount'),
      align: 'center',
      width: '110px',
      render: (row) => (
        <Chip
          size="small"
          label={row.itemCount}
          color={row.itemCount > 0 ? 'primary' : 'default'}
          variant={row.itemCount > 0 ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      id: 'optionGroupCount',
      label: t('stock.productMenusPage.columns.optionGroupCount'),
      align: 'center',
      width: '130px',
      render: (row) => (
        <Chip
          size="small"
          variant="outlined"
          color={row.optionGroupCount > 0 ? 'info' : 'default'}
          label={row.optionGroupCount}
        />
      ),
    },
    {
      id: 'allowNegativeStock',
      label: t('stock.productMenusPage.columns.negativeStock'),
      align: 'center',
      width: '120px',
      render: (row) => (
        <Chip
          size="small"
          variant="outlined"
          color={row.allowNegativeStock ? 'warning' : 'default'}
          label={
            row.allowNegativeStock
              ? t('stock.productMenusPage.negativeStock.allowed')
              : t('stock.productMenusPage.negativeStock.notAllowed')
          }
        />
      ),
    },
    {
      id: 'isActive',
      label: t('stock.productMenusPage.columns.status'),
      align: 'center',
      width: '100px',
      render: (row) => (
        <Chip
          size="small"
          color={row.isActive ? 'success' : 'default'}
          label={
            row.isActive
              ? t('stock.productMenusPage.status.active')
              : t('stock.productMenusPage.status.inactive')
          }
        />
      ),
    },
    {
      id: 'createdAt',
      label: t('stock.productMenusPage.columns.createdAt'),
      field: 'createdAt',
      sortable: true,
      width: '150px',
      render: (row) => <Typography variant="body2">{formatDate(row.createdAt)}</Typography>,
    },
    {
      id: 'actions',
      label: t('stock.productMenusPage.columns.actions'),
      align: 'center',
      width: '90px',
      render: (row) => (
        <Tooltip title={t('stock.productMenusPage.actions.edit')}>
          <span>
            <IconButton
              size="small"
              color="primary"
              disabled={openingMenuId === row.id}
              onClick={(e) => {
                e.stopPropagation();
                handleOpenMenu(row);
              }}
            >
              {openingMenuId === row.id ? <CircularProgress size={18} /> : <EditIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, locale, openingMenuId]);

  const hasFilter = debouncedSearchTerm.trim().length > 0 || statusFilter !== 'all';

  const searchField = (
    <TextField
      fullWidth
      size="small"
      placeholder={t('stock.productMenusPage.searchPlaceholder')}
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon />
          </InputAdornment>
        ),
      }}
    />
  );

  const statusSelect = (
    <FormControl fullWidth size="small">
      <InputLabel>{t('stock.productMenusPage.filters.status')}</InputLabel>
      <Select
        value={statusFilter}
        label={t('stock.productMenusPage.filters.status')}
        onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
      >
        <MenuItem value="all">{t('stock.productMenusPage.filters.all')}</MenuItem>
        <MenuItem value="active">{t('stock.productMenusPage.status.active')}</MenuItem>
        <MenuItem value="inactive">{t('stock.productMenusPage.status.inactive')}</MenuItem>
      </Select>
    </FormControl>
  );

  return (
    <>
      {/* Üst Başlık — stok yönetimi ekranıyla aynı düzen */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, md: 0 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: { xs: '100%', md: 'auto' } }}>
          <MenuBookIcon sx={{ fontSize: { xs: 28, md: 32 }, mr: 1.5, color: 'primary.main' }} />
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant={isMobile ? 'h6' : 'h5'} component="h1" fontWeight="bold">
                {t('stock.productMenusPage.title')}
              </Typography>
              <GuideButton guideKey="inventory.productMenusPage" size="small" />
            </Box>
            <Typography variant="body2" color="text.secondary">
              {t('stock.productMenusPage.subtitle')}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* İstatistik Kartları */}
      <StatsCardsContainer loading={summaryLoading && !summary}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<MenuBookIcon />}
            color={STATS_COLORS.green}
            title={`${summary?.totalCount ?? 0}`}
            subtitle={t('stock.productMenusPage.stats.total')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<CheckCircleIcon />}
            color={STATS_COLORS.blue}
            title={`${summary?.activeCount ?? 0}`}
            subtitle={t('stock.productMenusPage.stats.active')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<BlockIcon />}
            color={(summary?.inactiveCount ?? 0) > 0 ? STATS_COLORS.red : STATS_COLORS.grey}
            title={`${summary?.inactiveCount ?? 0}`}
            subtitle={t('stock.productMenusPage.stats.inactive')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<Inventory2Icon />}
            color={STATS_COLORS.purple}
            title={`${summary?.itemCount ?? 0}`}
            subtitle={t('stock.productMenusPage.stats.items')}
          />
        </Grid>
      </StatsCardsContainer>

      {/* Arama + filtre + aksiyonlar */}
      <Box sx={{ mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            {searchField}
          </Grid>
          <Grid item xs={12} md={3}>
            {statusSelect}
          </Grid>

          {/* Masaüstü butonlar */}
          <Grid item xs={12} md={5} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'flex-end' }}>
            <ButtonGroup>
              <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/stok')}>
                {t('stock.productMenusPage.actions.back')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                onClick={handleExportToExcel}
                disabled={menus.length === 0}
              >
                {t('stock.productMenusPage.actions.downloadExcel')}
              </Button>
              <Tooltip title={t('stock.productMenusPage.actions.refresh')}>
                <Button variant="outlined" onClick={handleRefresh} disabled={loading} sx={{ minWidth: 'auto', px: 1 }}>
                  <RefreshIcon />
                </Button>
              </Tooltip>
            </ButtonGroup>
          </Grid>

          {/* Mobil butonlar */}
          <Grid item xs={12} sx={{ display: { xs: 'grid', md: 'none' } }}>
            <Grid container spacing={1}>
              <Grid item xs={5}>
                <Button fullWidth size="small" variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/stok')}>
                  {t('stock.productMenusPage.actions.back')}
                </Button>
              </Grid>
              <Grid item xs={5}>
                <Button
                  fullWidth
                  size="small"
                  variant="outlined"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExportToExcel}
                  disabled={menus.length === 0}
                >
                  {t('stock.productMenusPage.actions.downloadExcel')}
                </Button>
              </Grid>
              <Grid item xs={2}>
                <Button fullWidth size="small" variant="outlined" onClick={handleRefresh} disabled={loading} sx={{ minWidth: 'auto', px: 0.5 }}>
                  <RefreshIcon fontSize="small" />
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* Aktif filtre bilgisi */}
        {hasFilter && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, mt: 1.5 }}>
            {debouncedSearchTerm.trim() && (
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label={`${t('stock.productMenusPage.filters.search')}: ${debouncedSearchTerm}`}
                onDelete={() => setSearchTerm('')}
              />
            )}
            {statusFilter !== 'all' && (
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label={
                  statusFilter === 'active'
                    ? t('stock.productMenusPage.status.active')
                    : t('stock.productMenusPage.status.inactive')
                }
                onDelete={() => setStatusFilter('all')}
              />
            )}
            <Button
              size="small"
              color="error"
              variant="text"
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('all');
              }}
            >
              {t('stock.productMenusPage.actions.clearFilters')}
            </Button>
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <InventoryStyleListTable<ProductMenuListItem>
        columns={columns}
        rows={menus}
        getRowId={(row) => row.id}
        loading={loading}
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        onSort={(field, direction) => {
          setSortColumn(field);
          setSortDirection(direction);
        }}
        sortField={sortColumn}
        sortDirection={sortDirection}
        clickable
        onRowClick={(row) => handleOpenMenu(row)}
        emptyMessage={
          hasFilter
            ? t('stock.productMenusPage.emptyFiltered')
            : t('stock.productMenusPage.empty')
        }
        ariaLabel={t('stock.productMenusPage.title')}
        columnResizeStorageKey="product-menus"
        toolbarInfo={t('stock.productMenusPage.recordCount', { count: totalCount })}
      />

      <ProductMenuModal
        open={Boolean(modalItem)}
        onClose={() => setModalItem(null)}
        item={modalItem}
        inventoryItems={[]}
        companyId={companyId || ''}
        warehouses={warehouses}
        categories={categories}
        onSuccess={() => {
          fetchMenus();
          fetchSummary();
        }}
      />
    </>
  );
};

export default ProductMenus;
